import { useEffect, useMemo, useRef, useState } from 'react';
import { layoutCatalog } from '../navBuilder';
import './layout-dialog.css';

/**
 * Fenster zum Ein- und Ausblenden der Bereiche im Menü (Änderungswunsch vom 19.10.2026). Es dient zweimal: Das Unternehmen passt damit sein Layout an (Einrichtung → Branche), und der Betreiber
 * legt damit das Layout einer Branche fest. Ein Haken heißt: Der Bereich steht im Menü. Ausgeblendet ist nur das Menü, die Module bleiben lizenziert und die Seiten über ihre Adresse erreichbar.
 */
export function LayoutDialog({
  title,
  intro,
  hidden,
  groupLabels,
  renamable,
  saving,
  error,
  onSave,
  onReset,
  resetLabel,
  resetHint,
  onClose,
}: {
  title: string;
  intro: string;
  hidden: string[];
  /** Eigene Namen der Hauptbereiche (Schlüssel der Gruppe) */
  groupLabels: Record<string, string>;
  /** Nur der Betreiber darf Hauptbereiche umbenennen. Das Unternehmen sieht die Namen, kann sie aber nicht ändern. */
  renamable: boolean;
  saving: boolean;
  error: string | null;
  onSave: (hidden: string[], groupLabels: Record<string, string>) => void;
  /** Ohne Angabe gibt es keine Schaltfläche zum Zurücksetzen */
  onReset?: () => void;
  resetLabel?: string;
  resetHint?: string;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const catalog = useMemo(() => layoutCatalog(), []);
  const [off, setOff] = useState<Set<string>>(() => new Set(hidden));
  const [labels, setLabels] = useState<Record<string, string>>(groupLabels);

  useEffect(() => {
    const el = dialog.current;
    if (el && !el.open) el.showModal();
  }, []);

  const toggle = (key: string) =>
    setOff((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setGroup = (keys: string[], visible: boolean) =>
    setOff((current) => {
      const next = new Set(current);
      for (const k of keys) {
        if (visible) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  const known = new Set(catalog.flatMap((g) => g.items.map((i) => i.key)));
  // Ausgeblendete Schlüssel, die es im Menü nicht (mehr) gibt, bleiben erhalten, damit ein Layout nicht unbemerkt Einträge verliert
  const unknown = [...off].filter((k) => !known.has(k));
  const cleanLabels = Object.fromEntries(Object.entries(labels).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
  const sameLabels = JSON.stringify(Object.entries(cleanLabels).sort()) === JSON.stringify(Object.entries(groupLabels).sort());
  const changed = off.size !== hidden.length || hidden.some((k) => !off.has(k)) || !sameLabels;
  const visibleCount = catalog.reduce((n, g) => n + g.items.filter((i) => !off.has(i.key)).length, 0);

  return (
    <dialog ref={dialog} className="dialog dialog--wide" aria-labelledby="layout-title" onClose={onClose} onClick={(e) => { if (e.target === dialog.current && !saving) onClose(); }}>
      <div className="dialog__body layout-dialog">
        <h2 id="layout-title">{title}</h2>
        <p className="muted small">{intro}</p>
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        <p className="small"><strong>{visibleCount}</strong> von {known.size} Bereichen stehen im Menü.</p>

        <div className="layout-dialog__groups">
          {catalog.map((g) => {
            const keys = g.items.map((i) => i.key);
            const allOn = keys.every((k) => !off.has(k));
            return (
              <fieldset key={g.key} className="layout-dialog__group">
                <legend>{labels[g.key]?.trim() || g.label}</legend>
                {renamable && (
                  <label className="field layout-dialog__rename">
                    <span className="small muted">Name im Menü</span>
                    <input className="input" value={labels[g.key] ?? ''} placeholder={g.label} maxLength={40} disabled={saving} onChange={(e) => setLabels((l) => ({ ...l, [g.key]: e.target.value }))} />
                  </label>
                )}
                <label className="check layout-dialog__all">
                  <input type="checkbox" checked={allOn} onChange={() => setGroup(keys, !allOn)} disabled={saving} />
                  <span>Ganze Gruppe</span>
                </label>
                {g.items.map((i) => (
                  <label key={i.key} className="check">
                    <input type="checkbox" checked={!off.has(i.key)} onChange={() => toggle(i.key)} disabled={saving} />
                    <span>{i.label}</span>
                  </label>
                ))}
              </fieldset>
            );
          })}
        </div>
        <p className="muted small">Die Einrichtung steht immer im Menü, damit Sie jederzeit an diese Einstellung kommen.</p>

        <div className="form-actions">
          <button type="button" className="btn btn--primary" disabled={saving || !changed} onClick={() => onSave([...off, ...unknown.filter((k) => !off.has(k))], cleanLabels)}>
            {saving ? 'Wird gespeichert …' : 'Speichern'}
          </button>
          <button type="button" className="btn btn--secondary" disabled={saving} onClick={onClose}>Abbrechen</button>
          {onReset && (
            <button type="button" className="btn btn--ghost" disabled={saving} onClick={onReset} title={resetHint}>
              {resetLabel ?? 'Auf Standard zurücksetzen'}
            </button>
          )}
        </div>
        {onReset && resetHint && <p className="muted small">{resetHint}</p>}
      </div>
    </dialog>
  );
}
